import { conns } from '@cfg/axiosConfig.tsx';
import type {
  CancelSubscriptionPayload,
  CreatePlanPayload,
  CreateSubscriptionPayload,
  CustomerSubscriptionBase,
  CustomerSubscriptionDetail,
  CustomerSubscriptionFilters,
  CustomerSubscriptionListResponse,
  PauseSubscriptionPayload,
  RegisterPaymentPayload,
  RenewSubscriptionPayload,
  SubscriptionAvailabilityEntry,
  SubscriptionPlan,
  SubscriptionPlanFilters,
  SubscriptionPlanListResponse,
  SubscriptionUsage,
  SubscriptionUsageSummary,
  UpdatePlanPayload,
  UpdateSubscriptionPayload,
  UsageConsumePayload,
  UsageFilters,
  UsageHistoryResponse,
  SubscriptionAvailabilityForItem,
  SubscriptionAssignmentResponse,
  InitialPaymentOffer,
} from '@app-types/subscriptions.ts';

const PLANS_URL_KEY = 'erp:subscriptions:plans';
const PLANS_AVAILABLE_URL_KEY = 'erp:subscriptions:plans_available';
const SUBSCRIPTIONS_URL_KEY = 'erp:subscriptions:customer';
const SUBSCRIPTIONS_BY_CUSTOMER_URL_KEY = 'erp:subscriptions:customer_by_customer';
const USAGE_URL_KEY = 'erp:subscriptions:usage';

const resolvePagination = (filters?: { page?: number; page_size?: number }) => {
  const page = filters?.page && filters.page > 0 ? filters.page : 1;
  const page_size = filters?.page_size && filters.page_size > 0 ? filters.page_size : 20;
  const skip = (page - 1) * page_size;
  return { page, page_size, skip, limit: page_size };
};

const normalizePlanFilters = (filters?: SubscriptionPlanFilters) => {
  if (!filters) return {};
  const { page, page_size, skip, limit } = resolvePagination(filters);
  return {
    is_active: filters.is_active,
    billing_period: filters.billing_period,
    search: filters.search?.trim() || undefined,
    min_price: filters.min_price,
    max_price: filters.max_price,
    skip,
    limit,
    page,
    page_size,
  };
};

const normalizeSubscriptionFilters = (filters?: CustomerSubscriptionFilters) => {
  if (!filters) return {};
  const { page, page_size, skip, limit } = resolvePagination(filters);
  return {
    customer_id: filters.customer_id,
    plan_id: filters.plan_id,
    status: filters.status,
    billing_status: filters.billing_status,
    auto_renew: filters.auto_renew,
    start_date_from: filters.start_date_from,
    start_date_to: filters.start_date_to,
    next_billing_from: filters.next_billing_from,
    next_billing_to: filters.next_billing_to,
    skip,
    limit,
    page,
    page_size,
  };
};

type SubscriptionWithAvailability = CustomerSubscriptionBase & {
  availability: SubscriptionAvailabilityEntry[];
};

export const subscriptionsApi = {
  // Plans
  async getPlans(filters?: SubscriptionPlanFilters): Promise<SubscriptionPlanListResponse> {
    const params = normalizePlanFilters(filters);
    const { data } = await conns<SubscriptionPlanListResponse>(PLANS_URL_KEY).get(params);
    return data;
  },
  async getPlan(planId: number): Promise<SubscriptionPlan> {
    const { data } = await conns<SubscriptionPlan>(PLANS_URL_KEY, [planId]).get();
    return data;
  },
  async getAvailablePlans(limit = 50): Promise<SubscriptionPlan[]> {
    const { data } = await conns<SubscriptionPlan[]>(PLANS_AVAILABLE_URL_KEY).get({ limit });
    return data ?? [];
  },
  async createPlan(payload: CreatePlanPayload): Promise<SubscriptionPlan> {
    const { data } = await conns<SubscriptionPlan>(PLANS_URL_KEY).post(payload);
    return data;
  },
  async updatePlan(planId: number, payload: UpdatePlanPayload): Promise<SubscriptionPlan> {
    const { data } = await conns<SubscriptionPlan>(PLANS_URL_KEY, [planId]).put(payload);
    return data;
  },
  async deletePlan(planId: number, force?: boolean): Promise<boolean> {
    const { data } = await conns<{ deleted: boolean }>(PLANS_URL_KEY, [planId]).delete({
      force: force ?? false,
    });
    return Boolean(data?.deleted);
  },
  async addPlanItem(
    planId: number,
    payload: CreatePlanPayload['plan_items'][number],
  ): Promise<SubscriptionPlan> {
    const { data } = await conns<SubscriptionPlan>(PLANS_URL_KEY, [planId, 'items']).post(payload);
    return data;
  },
  async removePlanItem(planId: number, itemId: number): Promise<boolean> {
    const { data } = await conns<{ deleted: boolean }>(PLANS_URL_KEY, [planId, 'items', itemId]).delete();
    return Boolean(data?.deleted);
  },

  // Subscriptions
  async getSubscriptions(
    filters?: CustomerSubscriptionFilters,
  ): Promise<CustomerSubscriptionListResponse> {
    const params = normalizeSubscriptionFilters(filters);
    const { data } = await conns<CustomerSubscriptionListResponse>(SUBSCRIPTIONS_URL_KEY).get(params);
    return data;
  },
  async getSubscriptionDetail(
    subscriptionId: number,
    options?: { includePlan?: boolean; includeCycles?: boolean },
  ): Promise<CustomerSubscriptionDetail> {
    const { data } = await conns<CustomerSubscriptionDetail>(SUBSCRIPTIONS_URL_KEY, [subscriptionId]).get({
      include_plan: options?.includePlan ?? true,
      include_cycles: options?.includeCycles ?? true,
    });
    return data;
  },
  async getCustomerSubscriptions(customerId: number, includeInactive = false): Promise<CustomerSubscriptionBase[]> {
    const { data } = await conns<CustomerSubscriptionBase[]>(SUBSCRIPTIONS_BY_CUSTOMER_URL_KEY, [
      customerId,
      'subscriptions',
    ]).get({ include_inactive: includeInactive });
    return data ?? [];
  },
  async getActiveSubscriptions(customerId: number): Promise<CustomerSubscriptionBase[]> {
    return this.getCustomerSubscriptions(customerId, false);
  },
  async getActiveSubscriptionsWithAvailability(
    customerId: number,
  ): Promise<SubscriptionWithAvailability[]> {
    const subscriptions = await this.getActiveSubscriptions(customerId);
    const availabilityEntries = await Promise.all(
      subscriptions.map(async (sub) => {
        const availability = await this.getAvailableCredits(sub.id);
        return { ...sub, availability } satisfies SubscriptionWithAvailability;
      }),
    );
    return availabilityEntries;
  },
  async getAvailabilityForItem(
    customerId: number,
    itemId: number,
  ): Promise<SubscriptionAvailabilityForItem | null> {
    const subscriptions = await this.getActiveSubscriptionsWithAvailability(customerId);
    for (const subscription of subscriptions) {
      const entry = subscription.availability.find((availableEntry) => availableEntry.item_id === itemId);
      if (entry && (entry.unlimited || (entry.available ?? 0) > 0)) {
        return {
          ...entry,
          subscription_id: subscription.id,
          subscription_name: subscription.plan?.name,
        } satisfies SubscriptionAvailabilityForItem;
      }
    }
    return null;
  },
  async createSubscription(payload: CreateSubscriptionPayload): Promise<SubscriptionAssignmentResponse> {
    const { data } = await conns<SubscriptionAssignmentResponse>(SUBSCRIPTIONS_URL_KEY).post(payload);
    return data;
  },
  async getInitialPaymentOffer(subscriptionId: number): Promise<InitialPaymentOffer> {
    const { data } = await conns<InitialPaymentOffer>(SUBSCRIPTIONS_URL_KEY, [
      subscriptionId,
      'initial-payment-offer',
    ]).get();
    return data;
  },
  async deleteSubscription(subscriptionId: number): Promise<boolean> {
    const { data } = await conns<{ success: boolean }>(SUBSCRIPTIONS_URL_KEY, [subscriptionId]).delete();
    return Boolean(data?.success);
  },
  async renewSubscription(
    subscriptionId: number,
    payload?: RenewSubscriptionPayload,
  ): Promise<CustomerSubscriptionBase> {
    const { data } = await conns<CustomerSubscriptionBase>(SUBSCRIPTIONS_URL_KEY, [
      subscriptionId,
      'renew',
    ]).post(payload ?? {});
    return data;
  },
  async cancelSubscription(
    subscriptionId: number,
    payload: CancelSubscriptionPayload,
  ): Promise<CustomerSubscriptionBase> {
    const { data } = await conns<CustomerSubscriptionBase>(SUBSCRIPTIONS_URL_KEY, [
      subscriptionId,
      'cancel',
    ]).post(payload);
    return data;
  },
  async pauseSubscription(
    subscriptionId: number,
    payload: PauseSubscriptionPayload,
  ): Promise<CustomerSubscriptionBase> {
    const { data } = await conns<CustomerSubscriptionBase>(SUBSCRIPTIONS_URL_KEY, [
      subscriptionId,
      'pause',
    ]).post(payload);
    return data;
  },
  async reactivateSubscription(subscriptionId: number): Promise<CustomerSubscriptionBase> {
    const { data } = await conns<CustomerSubscriptionBase>(SUBSCRIPTIONS_URL_KEY, [
      subscriptionId,
      'reactivate',
    ]).post();
    return data;
  },
  async updateSubscription(
    subscriptionId: number,
    payload: UpdateSubscriptionPayload,
  ): Promise<CustomerSubscriptionBase> {
    const { data } = await conns<CustomerSubscriptionBase>(SUBSCRIPTIONS_URL_KEY, [subscriptionId]).put(payload);
    return data;
  },

  // Usage
  async getUsageSummary(
    subscriptionId: number,
    currentCycleId?: number,
  ): Promise<SubscriptionUsageSummary[]> {
    const { data } = await conns<SubscriptionUsageSummary[]>(USAGE_URL_KEY, [
      subscriptionId,
      'usage',
      'summary',
    ]).get({ cycle_id: currentCycleId });
    return data ?? [];
  },
  async getAvailableCredits(subscriptionId: number): Promise<SubscriptionAvailabilityEntry[]> {
    const { data } = await conns<Record<number, SubscriptionAvailabilityEntry>>(
      USAGE_URL_KEY,
      [subscriptionId, 'usage', 'available'],
    ).get();
    return Object.entries(data ?? {}).map(([itemId, entry]) => ({
      ...entry,
      item_id: Number(itemId),
      unlimited: entry.unlimited,
    }));
  },
  async getUsageHistory(
    subscriptionId: number,
    filters?: UsageFilters,
  ): Promise<UsageHistoryResponse> {
    const params = {
      ...filters,
      limit: filters?.limit ?? 20,
      skip: 0,
    };
    const { data } = await conns<UsageHistoryResponse>(USAGE_URL_KEY, [subscriptionId, 'usage']).get(params);
    return data;
  },
  async consumeCredits(
    subscriptionId: number,
    payload: UsageConsumePayload,
  ): Promise<SubscriptionUsage> {
    const body = { ...payload, source: payload.source ?? 'manual' };
    const { data } = await conns<SubscriptionUsage>(USAGE_URL_KEY, [subscriptionId, 'usage']).post(body);
    return data;
  },

  // Payments
  async registerPayment(
    subscriptionId: number,
    payload: RegisterPaymentPayload,
  ): Promise<CustomerSubscriptionBase> {
    const { idempotency_key, ...body } = payload;
    const { data } = await conns<CustomerSubscriptionBase>(SUBSCRIPTIONS_URL_KEY, [
      subscriptionId,
      'pay',
    ]).post(body, {
      headers: { 'Idempotency-Key': idempotency_key ?? crypto.randomUUID() },
    });
    return data;
  },
};
